/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {timeDiff, twelveHourTime} from '@ion/core/src/utils/DateUtil';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {SubscriptionIcon} from '@ion/dls/src/icons/SubscriptionIcon';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor, dlsLayout, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {SubscriptionDialog} from '@/components/dialogs/SubscriptionDialog';
import {loadSubscriptionPlans, showSubscriptionDialog} from '@/store/actions/dashboard';
import {subscriptionDialogSelector} from '@/store/selectors/dashboard-selectors';
import {SubscriptionDetailLabelTypes} from '../../types/types';
import {resolveValue} from '../../utils/ComponentUtils';

interface SubscriptionDetails {
  asset: CVSAssetType;
  labels: SubscriptionDetailLabelTypes;
}

const format = 'DD MMM YYYY';

export enum SubscriptStatus {
  active = 'active',
  inactive = 'inactive',
  expired = 'expired',
  stolen = 'stolen',
  cancelled = 'cancelled'
};

export const statusId = {
  1 : [SubscriptStatus.active],
  2 : [SubscriptStatus.expired],
  3 : [SubscriptStatus.stolen],
  4 : [SubscriptStatus.cancelled]
};

export const warningStatus = [
  SubscriptStatus.cancelled,
  SubscriptStatus.expired,
  SubscriptStatus.inactive,
  SubscriptStatus.stolen
];

export const isWarningStatus = (status: SubscriptStatus) => warningStatus.indexOf(status) > -1;

const useStyles = makeStyles((theme: DLSTheme) => ({
  content : {
    paddingLeft  : dlsLayout('gutters', 'mobile'),
    paddingRight : dlsLayout('gutters', 'mobile') + 10,
    paddingTop   : 0
  },
  alignment : {
    display    : 'inline-flex',
    alignItems : 'center'
  },
  subscriptions : {
    ...dlsTypography('panelCards'),
    color     : dlsColor('grey2'),
    minHeight : 64
  },
  purchaseClass : {
    fontSize    : '14px',
    color       : dlsColor('caBlue'),
    paddingLeft : '24px',
    paddingTop  : '30px'
  },
  active : {
    color           : dlsColor('caGreen'),
    backgroundColor : 'rgba(105, 178, 70, 0.1)',
    padding         : '4px',
    fontSize        : '14px'
  },
  status : {
    color           : dlsColor('caRed'),
    backgroundColor : 'rgba(210, 0, 0, 0.1)',
    padding         : '4px',
    fontSize        : '14px'
  },
  rightAlign : {
    textAlign : 'right'
  }
}));

export function getStatus(plan) {
  if (plan?.statusId) {
    return statusId[plan.statusId][0];
  }
  return SubscriptStatus.inactive;
}

function isDue(plan): boolean {
  if (plan) {
    const today  = new Date();
    const expiry = new Date(plan.expiryDate);

    return timeDiff(expiry, today) <= 30;
  }
  return false;
}

/**
 * The content of the list items in the Subscription card differ in content but
 * share styles depending on what's being displayed. So we can reuse it with
 * this component.
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import SubscriptionDetails from '@ion/dls/src/components/cards/SubscriptionDetails';
 *   import { theme } from '@ion/dls/src/theme/theme';
 *
 *   <SubscriptionDetails asset={asset}
 *     labels={labels} />
 *
 * ```
 *
 */
const SubscriptionDetails = ({asset: {subscriptionData, vin}, labels}: SubscriptionDetails) => {
  const classes                    = useStyles();
  const dispatch                   = useDispatch();
  const {currentPlan, renewalPlan} = subscriptionData;
  const subscriptionDialogShow     = useSelector(subscriptionDialogSelector);
  const status                     = useMemo(() => getStatus(currentPlan), [currentPlan]);
  const [showSnack, setShowSnack]  = useState(
    warningStatus.filter(value => status === value)?.length > 0
  );
  const [expanded, setExpanded]    = useState(false);
  const dueSoon                    = isDue(currentPlan);
  const canRenew                   = (!!currentPlan?.canRenew && status !== SubscriptStatus.stolen && !renewalPlan) ||
                                      status === SubscriptStatus.inactive;

  function launchSubscriptionDialog() {
    dispatch(loadSubscriptionPlans(vin));
    dispatch(showSubscriptionDialog(true));
    setShowSnack(false);
  };

  const onDismiss = useCallback(function() {
    setShowSnack(false);
  }, []);

  const onToggleExpand = useCallback(function(isExpand) {
    setExpanded(isExpand);
  }, []);

  return (
    <>
      <Accordion children=''
        expanded={expanded}
        onExpand={onToggleExpand}
        header={
          <div className={classes.alignment}>
            <div className={classes.subscriptions}>
              {translate('sidesheet:subscriptionCard.header')}
            </div>
          </div>
        }
        details={
          <><div className={classes.content}>
            <CardItemRow label={labels.status}
              isLabelColon={false}
              isCenterAligned={false}
              value={<div className={status === SubscriptStatus.active ? classes.active : classes.status}>
                {translate(`sidesheet:subscriptionDetails.statusValues.${status}`).toUpperCase()}
              </div>} />
            {currentPlan && <>
              <CardItemRow label={labels.plan}
                isLabelColon={false}
                isCenterAligned={false}
                value={<div className={classes.rightAlign}>{resolveValue(currentPlan?.name)}</div>} />
              {status !== SubscriptStatus.stolen && <CardItemRow isLabelColon={false}
                label={status === SubscriptStatus.cancelled ? labels.cancelledDate : labels.expiryDate}
                isCenterAligned={false}
                value={twelveHourTime(
                  currentPlan?.[status === SubscriptStatus.cancelled ? 'cancellationDate' : 'expiryDate'], format
                )} />}
            </>}
            {renewalPlan && <>
              <CardItemRow label={labels.upcomingPlan}
                isLabelColon={false}
                isCenterAligned={false}
                value={<div className={classes.rightAlign}>{resolveValue(renewalPlan?.name)}</div>} />
              <CardItemRow label={labels.upcomingPlanStatus}
                isLabelColon={false}
                isCenterAligned={false}
                value={<div className={classes.active}>
                  {translate('sidesheet:subscriptionDetails.purchased').toUpperCase()}
                </div>} />
              <CardItemRow label={labels.renewalDate}
                isLabelColon={false}
                isCenterAligned={false}
                value={twelveHourTime(renewalPlan?.startDate, format)} />
            </>}
            {(dueSoon && status === SubscriptStatus.active && !renewalPlan) && <CardItemRow label={labels.note}
              isLabelColon={false}
              isCenterAligned={false}
              value={<div style={{color: dlsColor('caRed')}}>{translate('sidesheet:subscriptionDetails.dueSoon')}</div>} />}
          </div>
          {!renewalPlan && canRenew && (
            <>
              <Divider />
              <div style={{height: '40px'}}>
                <TertiaryButton className={classes.purchaseClass}
                  startIcon={<SubscriptionIcon color={dlsColor('caBlue')}/>}
                  onClick={launchSubscriptionDialog}>
                  <span>{translate('sidesheet:subscriptionCard.purchaseSubscription')}</span>
                </TertiaryButton>
              </div>
            </>
          )}
          </>
        } />
      {subscriptionDialogShow && <SubscriptionDialog />}
      {<IonSnackbar show={showSnack}
        onAction={canRenew && launchSubscriptionDialog}
        message={translate(`sidesheet:snackMessage.${status}`)}
        type={'error'}
        actionLabel={canRenew && translate('sidesheet:purchase')}
        customDismissLabel={translate('sidesheet:dismiss')}
        onDismiss={onDismiss} />
      }
    </>
  );
};

export default SubscriptionDetails;
